import { z } from 'zod'

/**
 * Keyset pagination everywhere. OFFSET degrades linearly with page depth and D1
 * gives no help there — see docs/02-architecture.md §7.
 *
 * The cursor is an opaque base64url of `${sortValue}|${id}`; callers must treat it
 * as a token, never parse it.
 */

// Divisible by every column count the grid view uses (1, 2, 3), so a page that has
// a "Show more" under it never ends on a half-empty row.
export const DEFAULT_PAGE_SIZE = 24
export const MAX_PAGE_SIZE = 100

export const pageQuerySchema = z.object({
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
})
export type PageQuery = z.infer<typeof pageQuerySchema>

export interface Page<T> {
  items: T[]
  nextCursor: string | null
}

export function pageSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  })
}

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(s: string): string {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4)
  const bin = atob(padded)
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

export function encodeCursor(sortValue: string | number, id: string): string {
  return toBase64Url(`${sortValue}|${id}`)
}

export function decodeCursor(cursor: string): { sortValue: string; id: string } | null {
  try {
    const raw = fromBase64Url(cursor)
    const sep = raw.lastIndexOf('|')
    if (sep < 1) return null
    return { sortValue: raw.slice(0, sep), id: raw.slice(sep + 1) }
  } catch {
    return null
  }
}
